/**
 * Shared header for the inventory tool family (issue #507): page title, tabs
 * linking the sibling inventory pages, and — everywhere but Inventory
 * Imports, which is fleet-wide — the HQ store picker (`HqStoreSelect` from
 * Today.tsx). Used at the top of Inventory Count, Inventory variance, and
 * Variance history so the picker sits in the identical spot on all three, and
 * every tab link carries the current store so switching tabs never loses it.
 *
 * Tab styling mirrors CoasTabs.tsx (compliance/CoasTabs.tsx): Link + active
 * underline, the closest existing pattern for a page-level tab group.
 */

import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSession } from '@/lib/useSession';
import { hasAccessSlug } from '@/lib/toolVisibility';
import { hasStoresStore } from '@/lib/useStores';
import { StoresHeader } from './storesShared';
import { HqStoreSelect } from './Today';

const TABS: {
  to: string;
  label: string;
  check: (perms: string[] | undefined, superAdmin: boolean) => boolean;
}[] = [
  { to: '/stores/inventory', label: 'Inventory Count', check: (p, s) => hasStoresStore(p, s) },
  {
    to: '/stores/hq/inventory',
    label: 'Inventory variance',
    check: (p, s) => hasAccessSlug(p, 'stores.variance', s),
  },
  {
    to: '/stores/hq/inventory-history',
    label: 'Variance history',
    check: (p, s) => hasAccessSlug(p, 'stores.variance', s),
  },
  {
    to: '/stores/hq/imports',
    label: 'Inventory Imports',
    check: (p, s) => hasAccessSlug(p, 'stores.imports', s),
  },
];

export function InventoryStoreHeader({
  title,
  subtitle,
  actions,
  showStorePicker = true,
  allowAllStores = false,
  date,
  storeNo = null,
  onStoreChange,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  showStorePicker?: boolean;
  allowAllStores?: boolean;
  date?: string;
  storeNo?: number | null;
  onStoreChange?: (storeNo: number | null) => void;
}) {
  const { user } = useSession();
  const location = useLocation();
  const search = storeNo !== null ? `?store=${storeNo}` : '';
  const visibleTabs = TABS.filter((tab) => tab.check(user?.permissions, !!user?.superAdmin));

  return (
    <div className="mb-6">
      <StoresHeader title={title} subtitle={subtitle} actions={actions} />

      {visibleTabs.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-1 border-b border-amp-line dark:border-amp-dark-line">
          {visibleTabs.map((tab) => {
            const isActive = location.pathname === tab.to;
            return (
              <Link
                key={tab.to}
                to={{ pathname: tab.to, search }}
                className={`-mb-px border-b-2 px-3 py-2 text-sm ${
                  isActive
                    ? 'border-amp-ink text-amp-ink font-medium dark:border-amp-dark-ink dark:text-amp-dark-ink'
                    : 'border-transparent text-amp-ink-muted hover:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink'
                }`}
              >
                {tab.label}
              </Link>
            );
          })}
        </div>
      )}

      {showStorePicker && date !== undefined && onStoreChange && (
        <div className="max-w-xs">
          <HqStoreSelect date={date} value={storeNo} onChange={onStoreChange} allowAll={allowAllStores} />
        </div>
      )}
    </div>
  );
}
